import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import CountUp from "react-countup";
import useEmblaCarousel from "embla-carousel-react";
import {
  Activity,
  Anchor,
  ArrowRight,
  Award,
  Baby,
  Instagram,
  Layers,
  MapPin,
  Navigation,
  Phone,
  ScanLine,
  ShieldCheck,
  Smile,
  Sparkles,
  Star,
  Stethoscope,
} from "lucide-react";
import {
  CLINIC_ADDRESS,
  CLINIC_HOURS,
  CLINIC_PHONE_DISPLAY,
  DOCTORS,
  GALLERY_ITEMS,
  INSTAGRAM_URL,
  LAB_IMAGES,
  MAP_DIRECTIONS_URL,
  MAP_EMBED_SRC,
  TECH_ITEMS,
  WHATSAPP_CHAT_URL,
} from "../config";
import { Reveal, SectionLabel } from "../components/Reveal";
import { DoctorAvatar } from "../components/DoctorAvatar";

const ICONS = { Activity, Smile, Sparkles, Layers, Anchor, Baby, ScanLine };

const SECTION_H2 = "font-heading text-3xl text-[#14717A] sm:text-4xl lg:text-[2.5rem] lg:leading-[1.15]";

const PREVIEW_SERVICES = [
  { icon: "Activity", title: "Root Canal Treatment", desc: "Painless, single-visit root canals that save your natural tooth." },
  { icon: "Smile", title: "Smile Design", desc: "A digitally planned smile makeover crafted around your face." },
  { icon: "Sparkles", title: "Teeth Whitening & Cleaning", desc: "Gentle cleaning and whitening that refresh your smile instantly." },
  { icon: "Anchor", title: "Dental Implants", desc: "Permanent, natural-feeling replacements for missing teeth." },
];

const STATS = [
  { end: 10, suffix: "+", label: "Years of Experience" },
  { end: 5, suffix: "/5", decimals: 0, label: "Average Google Rating", star: true },
  { end: 99, suffix: "%", label: "Patients Who Returned" },
  { end: 4000, suffix: "+", label: "Patients Treated" },
];

const WHY_CARDS = [
  { icon: Award, title: "Experienced Dentists", desc: "Our team includes highly skilled dentists who stay updated with the latest techniques and best practices in dentistry." },
  { icon: ScanLine, title: "Latest Digital Technology", desc: "We use digital X-rays, intraoral scanning and 3D imaging for fast, accurate and comfortable diagnosis." },
  { icon: Stethoscope, title: "Comprehensive Care Under One Roof", desc: "From routine checkups to implants and full-mouth rehabilitation — we handle all your dental needs." },
  { icon: ShieldCheck, title: "Strict Hygiene & Safety Standards", desc: "Our clinic follows rigorous sterilisation and pre-cleaning disinfection to ensure a safe environment." },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const AutoSlider = ({ images, height = "h-64 sm:h-80", width = "max-w-xl" }) => {
  const [ref, api] = useEmblaCarousel({ loop: true });
  useEffect(() => {
    if (!api) return;
    const t = setInterval(() => api.scrollNext(), 3200);
    return () => clearInterval(t);
  }, [api]);
  return (
    <div className={`mx-auto w-full ${width} overflow-hidden rounded-3xl shadow-[0_24px_60px_rgba(20,113,122,0.18)]`} ref={ref}>
      <div className="flex">
        {images.map((img, i) => (
          <div key={i} className={`relative min-w-0 flex-[0_0_100%] ${height} bg-white`}>
            <img
              src={img.src || img}
              alt={`Slide ${i + 1}`}
              className={`h-full w-full ${img.contain ? "object-contain p-8" : "object-cover"}`}
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const FALLBACK_REVIEWS = [
  { name: "Vaibhav Panchal", text: "Good tritment", rating: 5, when: "Aug 21, 2026" },
  { name: "Sarika", text: "Handles the patient very patiently. Recently I have taken the treatment — excellent treatment is given by respected sir.", rating: 5, when: "Aug 18, 2026" },
  { name: "Supriya Devkar", text: "Clinic ambience is very good and clean. Dr. Vishwa is very polite and friendly. Before treatment he explained nicely. Waiting area is available, reception helps with appointments, and all dental apparatus are well maintained.", rating: 5, when: "Aug 04, 2026" },
  { name: "Mayur Kulkarni", text: "I felt like it was a very good treatment. The doctor gave very good information. The clinic is clean and tidy. I am also recommending other people to get treatment here.", rating: 5, when: "Jul 15, 2026" },
  { name: "Chetan Chaubisa", text: "I have taken treatment of tooth removal from the doctor. He is very kind, humble & knowledgeable. He made me understand aftereffects and precautions. The whole process was very smooth. Highly recommend his treatment.", rating: 5, when: "May 15, 2026" },
  { name: "Shekhar Karkamkar", text: "A very nice & skillful hand. Fully equipped clinic. Doctor is very friendly.", rating: 5, when: "May 01, 2026" },
  { name: "Varsha Benure", text: "Dr. Vishwa is an excellent dentist. I had a good treatment without any pain. He is very polite and professional. Treatments are affordable. His clinic was very clean and hygiene was maintained.", rating: 5, when: "Apr 03, 2026" },
  { name: "Bagyashree Tuppad", text: "Experienced doctor, handles with necessary skills. Very good doctor with excellence in work.", rating: 5, when: "Mar 11, 2026" },
  { name: "Kotresh M", text: "Recently I underwent RCT treatment from Dr. Vishwa sir's clinic and had good treatment without much pain. He explained each and every step to me. I suggest this clinic for all types of dental problems. Thank you very much sir.", rating: 5, when: "Mar 03, 2026" },
  { name: "Anastasiya Yovkova", text: "I would highly recommend Dr. Vishwa as an excellent dentist with such a gentle and caring approach. The treatment was completely painless and I felt very comfortable throughout. Truly grateful for the warm care and professionalism.", rating: 5, when: "Jan 14, 2026" },
  { name: "Praveen Telang", text: "Brilliant dentist! Very professional and courteous. Dr. Vishwa explained everything using simple language and his hand is very skilled. Highly recommended!", rating: 5, when: "Jan 13, 2026" },
  { name: "Kiran Waghmare", text: "I have visited Dr. Vishwa Dental — my experience was nice. I went for my tooth extraction, it was not painful and I was happy with the treatment. Thank you.", rating: 5, when: "Nov 29, 2025" },
  { name: "Sagar Nikam", text: "I recently got a root canal done here, and it was a truly great experience. The entire procedure was painless and precisely done. The doctor was extremely polite and professional, explained every step clearly. Highly recommend this clinic!", rating: 5, when: "Oct 08, 2025" },
  { name: "Bhagyashri Ambulage", text: "Good service", rating: 5, when: "Oct 01, 2025" },
  { name: "Pandurang Sutar", text: "Very best service", rating: 5, when: "Sep 17, 2025" },
];

const AVATAR_COLORS = ["#14717A", "#4CA6AF", "#7a5fa0", "#c07a4c", "#4c7ac0"];

const GoogleG = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const Reviews = () => {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);
  useEffect(() => {
    fetch("https://embedsocial.com/api/widget_items/190695/")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        const items = (d.media || [])
          .filter((m) => m.caption)
          .map((m) => ({
            name: m.authorName || "Patient",
            text: m.caption,
            rating: m.rating || 5,
            when: m.mediaCreatedOn || "",
          }));
        if (items.length) setReviews(items);
      })
      .catch(() => {});
  }, []);
  const loop = [...reviews, ...reviews];
  return (
    <section id="reviews" data-testid="reviews-section" className="overflow-hidden bg-white py-14 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <Reveal>
          <SectionLabel text="Review and Feedback" />
          <div className="flex flex-wrap items-center justify-start gap-4 text-left">
            <h2 className="font-sans text-2xl font-normal text-[#4CA6AF] sm:text-3xl">Our Patient Speak's</h2>
            <div
              data-testid="google-rating-badge"
              className="flex items-center gap-2 rounded-full border-2 border-[#DCE4E8] bg-white px-5 py-2.5 shadow-sm"
            >
              <GoogleG className="h-5 w-5" />
              <span className="text-base font-bold text-[#0f2b2e]">5.0</span>
              <Star className="h-5 w-5 fill-[#F4B400] text-[#F4B400]" />
              <span className="text-sm font-medium text-[#8A9CAB]">on Google</span>
            </div>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="mt-10 overflow-hidden" data-testid="reviews-marquee">
          <div className="marquee-track flex w-max gap-5 px-6" style={{ animationDuration: "90s" }}>
            {loop.map((r, i) => (
              <div
                key={`${r.name}-${i}`}
                data-testid={`review-card-${i % reviews.length}`}
                className="flex w-[300px] shrink-0 flex-col rounded-3xl border border-[#DCE4E8] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(20,113,122,0.12)] sm:w-[340px]"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                    style={{ backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                  >
                    {r.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-[#0f2b2e]">{r.name}</p>
                    <p className="text-xs text-[#8A9CAB]">{r.when}</p>
                  </div>
                  <GoogleG className="h-4 w-4 shrink-0" />
                </div>
                <div className="mt-3 flex gap-0.5">
                  {[...Array(5)].map((_, s) => (
                    <Star
                      key={s}
                      className={`h-4 w-4 ${s < r.rating ? "fill-[#F4B400] text-[#F4B400]" : "text-[#DCE4E8]"}`}
                    />
                  ))}
                </div>
                <p className="mt-3 line-clamp-5 text-sm leading-relaxed text-[#5f7482]">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

const WhyChooseUs = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((a) => (a + 1) % WHY_CARDS.length), 2200);
    return () => clearInterval(t);
  }, [paused]);
  return (
    <section id="why-us" data-testid="why-us-section" className="bg-white py-14 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <Reveal className="max-w-3xl">
          <SectionLabel text="Why Choose Us" />
          <h2 className={SECTION_H2}>
            Choosing the right clinic is about trust, expertise and comfort.
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_CARDS.map((c, i) => {
            const Icon = c.icon;
            const on = i === active;
            return (
              <Reveal key={c.title} delay={i * 0.08}>
                <div
                  data-testid={`why-card-${i}`}
                  onMouseEnter={() => { setPaused(true); setActive(i); }}
                  onMouseLeave={() => setPaused(false)}
                  className={`h-full rounded-3xl border border-[#4CA6AF]/30 border-l-[6px] border-l-[#14717A] p-5 shadow-[0_0_0_3px_rgba(76,166,175,0.12),0_14px_34px_rgba(76,166,175,0.22)] transition-all duration-500 ${
                    on
                      ? "-translate-y-2 bg-white shadow-[0_0_0_4px_rgba(76,166,175,0.2),0_20px_44px_rgba(76,166,175,0.3)]"
                      : "bg-[#fbfdfe]"
                  }`}
                >
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-2xl transition-colors duration-500 ${
                      on ? "bg-[#14717A] text-white" : "bg-[#DCE4E8] text-[#14717A]"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-sans text-base font-semibold text-[#0f2b2e]">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#8A9CAB]">{c.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const TreatmentGallery = () => (
  <section id="patient-stories" data-testid="patient-stories-section" className="bg-[#f2f6f7] py-14 sm:py-20">
    <div className="mx-auto max-w-7xl px-6 sm:px-8">
      <Reveal>
        <SectionLabel text="Treatment Gallery" />
        <h2 className={SECTION_H2}>Patient Transformations</h2>
      </Reveal>
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-2 gap-5 sm:grid-cols-3 sm:gap-6" data-testid="stories-grid">
        {GALLERY_ITEMS.slice(0, 6).map((g, i) => (
          <Reveal key={g.src} delay={(i % 3) * 0.08}>
            <div className="group relative overflow-hidden rounded-2xl shadow-sm" data-testid={`story-image-${g.caseNo}`}>
              <img
                src={g.src}
                alt="Patient transformation"
                loading="lazy"
                className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 text-center">
        <Link
          to="/stories"
          data-testid="view-all-stories-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-[#14717A] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0d5259]"
        >
          View All
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative flex min-h-[100svh] flex-col overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img src="/assets/hero.jpg" alt="Dr. Vishwa's Dental Hospital clinic" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d4a51]/95 via-[#14717A]/80 to-[#14717A]/30" />

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pb-40 pt-36 sm:px-8">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-welcome"
            className="text-lg font-semibold tracking-[0.12em] text-[#9fd8dd] sm:text-xl"
          >
            Welcome to Dr. Vishwa's Dental Hospital
          </motion.p>
          <h1 className="mt-6 text-3xl font-bold leading-[1.12] text-white sm:text-4xl lg:text-5xl" data-testid="hero-headline">
            <MaskedLine delay={0.3}>Crafting Healthy &</MaskedLine>
            <MaskedLine delay={0.45}>Confident Smiles</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            data-testid="hero-subheadline"
            className="mt-6 max-w-xl text-base text-white/85 sm:text-lg"
          >
            Well-equipped and certified dental clinic in Solapur.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/book"
              data-testid="hero-book-btn"
              className="rounded-full bg-[#4CA6AF] px-8 py-4 text-sm font-bold text-white shadow-[0_12px_30px_rgba(76,166,175,0.45)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#14717A]"
            >
              Book Appointment
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="group flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:border-white hover:bg-white/10"
            >
              Explore Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          data-testid="hero-stats"
          className="absolute bottom-0 left-0 right-0 z-10 bg-[#14717A]/95 backdrop-blur"
        >
          <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <div key={s.label} className="px-4 py-6 text-center sm:px-6" data-testid={`stat-${i}`}>
                <p className="flex items-center justify-center gap-2 font-heading text-3xl font-semibold text-white sm:text-4xl">
                  <CountUp end={s.end} suffix={s.suffix} decimals={s.decimals || 0} duration={2.2} enableScrollSpy scrollSpyOnce />
                  {s.star && <Star className="h-6 w-6 fill-[#F4B400] text-[#F4B400]" />}
                </p>
                <p className="mt-1 text-[11px] font-bold tracking-[0.18em] uppercase text-white/70 sm:text-xs">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div data-testid="marquee" className="overflow-hidden border-b border-[#DCE4E8] bg-white py-4">
        <div className="marquee-track flex w-max items-center gap-8 whitespace-nowrap">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-8">
              {["Root Canal", "Smile Design", "Dental Implants", "Invisalign", "Teeth Whitening", "Kids Dentistry", "Crowns & Bridges", "Full Mouth Rehabilitation"].map((t) => (
                <span key={t} className="flex items-center gap-8 font-heading text-lg italic text-[#4CA6AF] sm:text-xl">
                  {t}
                  <span className="h-1.5 w-1.5 rounded-full bg-[#DCE4E8]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ABOUT CLINIC */}
      <section id="about-clinic" data-testid="about-clinic-section" className="bg-white py-14 font-sans sm:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 sm:px-8 lg:grid-cols-[5fr_6fr] lg:gap-16">
          <Reveal>
            <div className="mx-auto w-full max-w-md">
              <img
                src="/assets/clinic.jpg"
                alt="Inside Dr. Vishwa's Dental Hospital"
                data-testid="about-clinic-photo-1"
                className="aspect-[4/3] w-full rounded-3xl object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <SectionLabel text="About the Clinic" />
            <h2 className="font-sans text-3xl font-semibold text-[#14717A] sm:text-4xl lg:text-[2.5rem] lg:leading-[1.15]">
              Precise Diagnosis, Best-in-class Care, Backed by Technology
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#5f7482]">
              We bring a patient-first approach to every visit — one built on
              thorough explanations. We use advanced technology to ensure fast
              and accurate diagnosis and provide personalised treatment.
            </p>
            <p className="mt-3 text-base leading-relaxed text-[#5f7482]">
              We provide dental services from routine checkups and tooth
              cleaning to advanced treatments like root canals, crowns, bridges,
              dental implants and cosmetic dentistry — all under one roof. Our
              clinic is equipped with digital X-ray, intraoral scanning, 3D
              printed models, CBCT analysis and advanced CAD/CAM restorations.
            </p>
            <Link
              to="/book"
              data-testid="about-clinic-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#14717A] px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0d5259]"
            >
              Plan Your Visit <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TEAM */}
      <section id="team" data-testid="team-section" className="bg-[#f2f6f7] py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <Reveal className="flex flex-col items-center text-center">
            <SectionLabel text="Our Team" />
            <h2 className="font-heading text-2xl text-[#14717A] sm:text-3xl lg:text-4xl">Meet Our Specialist Doctors</h2>
          </Reveal>
          <div className="mx-auto mt-10 grid max-w-5xl gap-10 sm:grid-cols-2">
            {DOCTORS.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.08}>
                <div
                  data-testid={`doctor-card-${i}`}
                  className="group flex h-full flex-col items-center rounded-2xl bg-white p-6 text-center shadow-[0_8px_30px_rgba(20,113,122,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(20,113,122,0.14)]"
                >
                  {d.photo ? (
                    <img
                      src={d.photo}
                      alt={d.name}
                      className="max-h-56 w-auto max-w-full rounded-2xl object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-56 w-full items-end justify-center rounded-2xl bg-[#eef3f4]">
                      <DoctorAvatar className="mb-5 h-32 w-32 rounded-full" />
                    </div>
                  )}
                  <div className="pt-5">
                    <h3 className="text-lg font-semibold text-[#0f2b2e] sm:text-xl">{d.name}</h3>
                    <p className="mt-1 text-xs font-bold tracking-wide text-[#4CA6AF] sm:text-sm">{d.designation}</p>
                    <p className="mt-2 text-base leading-relaxed text-[#8A9CAB]">{d.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LAB PARTNERS */}
      <section id="lab" data-testid="lab-section" className="bg-white py-14 sm:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 sm:px-8 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <SectionLabel text="Partnered Lab" />
            <p className="mt-5 text-base font-semibold leading-relaxed text-[#22444a]">
              Collaborated with Dental Craft Laboratory, located in South
              Mumbai, having 16 years of experience.
            </p>
            <p className="mt-3 text-base leading-relaxed text-[#5f7482]">
              Our partnered lab uses advanced CAD/CAM technology, premium
              materials and strict quality control to craft crowns, bridges,
              dentures and veneers that fit perfectly and look natural.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {["CAD/CAM Crafted", "Premium Materials"].map((t) => (
                <span key={t} className="rounded-full bg-[#f2f6f7] px-4 py-2 text-xs font-bold tracking-wide text-[#14717A]">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <AutoSlider images={LAB_IMAGES} height="h-52 sm:h-64" width="max-w-md" />
          </Reveal>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section id="services-preview" data-testid="services-preview-section" className="bg-[#f2f6f7] py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <Reveal>
            <SectionLabel text="Treatments" />
            <h2 className={SECTION_H2}>Care for Every Smile</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PREVIEW_SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div
                    data-testid={`preview-service-${i}`}
                    className="group h-full rounded-3xl border border-[#4CA6AF]/30 border-l-[6px] border-l-[#14717A] bg-white p-7 shadow-[0_0_0_3px_rgba(76,166,175,0.12),0_14px_34px_rgba(76,166,175,0.22)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_0_0_4px_rgba(76,166,175,0.2),0_20px_44px_rgba(76,166,175,0.3)]"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f2f6f7] text-[#14717A] transition-colors duration-500 group-hover:bg-[#14717A] group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-sans text-lg font-semibold text-[#0f2b2e]">{s.title}</h3>
                    <p className="mt-2 text-base leading-relaxed text-[#8A9CAB]">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-8 text-center">
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#14717A] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0d5259]"
            >
              View All Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ABOUT US */}
      <section id="about-us" data-testid="about-us-section" className="bg-white py-14 sm:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 sm:px-8 lg:grid-cols-[5fr_6fr] lg:gap-16">
          <Reveal>
            <AutoSlider images={["/assets/hero.jpg", "/assets/clinic.jpg"]} />
          </Reveal>
          <Reveal delay={0.15}>
            <SectionLabel text="About Us" />
            <p className="mt-5 text-base leading-relaxed text-[#5f7482]">
              At Dr. Vishwa's Dental Hospital, we are committed to providing
              high-quality dental care for patients of all ages in Solapur and
              surrounding areas. Our team of experienced dentists and support
              staff combines expertise with a patient-first approach to ensure
              every visit is comfortable, efficient and result-oriented.
            </p>
            <p className="mt-3 text-base leading-relaxed text-[#5f7482]">
              We believe in honest treatment plans, transparent pricing and
              gentle care. Whether you need a simple cleaning or advanced
              cosmetic work, our goal is the same: to help you maintain a
              healthy and confident smile.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ADVANCED TECHNOLOGY */}
      <section id="technology" data-testid="technology-section" className="bg-[#f2f6f7] py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <Reveal>
            <SectionLabel text="Advanced Dental Technology" />
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TECH_ITEMS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <div
                  data-testid={`tech-card-${i}`}
                  className="group h-full rounded-3xl border border-[#4CA6AF]/30 border-l-[6px] border-l-[#14717A] bg-white p-6 shadow-[0_0_0_3px_rgba(76,166,175,0.12),0_14px_34px_rgba(76,166,175,0.22)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_0_0_4px_rgba(76,166,175,0.2),0_20px_44px_rgba(76,166,175,0.3)]"
                >
                  <h3 className="text-xl font-bold text-[#14717A]">{t.name}</h3>
                  <img
                    src={t.src}
                    alt={t.name}
                    data-testid={`tech-image-${i}`}
                    loading="lazy"
                    className="mt-4 h-44 w-full rounded-2xl object-cover transition-transform duration-700 group-hover:scale-[1.03] sm:h-48"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Reviews />

      <TreatmentGallery />

      <WhyChooseUs />

      {/* CONTACT */}
      <section id="contact" data-testid="contact-section" className="bg-[#f2f6f7] py-14 sm:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 sm:px-8 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <SectionLabel text="Contact Us" />
            <div className="mt-7 space-y-5">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-[#14717A] shadow-sm">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#8A9CAB]">Visit Us At</p>
                  <p className="mt-1 text-sm font-medium text-[#22444a] sm:text-base" data-testid="contact-address">{CLINIC_ADDRESS}</p>
                  <p className="mt-1 text-sm text-[#8A9CAB]">Open daily · {CLINIC_HOURS}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-[#14717A] shadow-sm">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#8A9CAB]">Call Us At</p>
                  <a href="tel:+919096567419" data-testid="contact-phone" className="mt-1 block text-sm font-medium text-[#22444a] transition-colors duration-200 hover:text-[#14717A] sm:text-base">
                    {CLINIC_PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                data-testid="contact-book-btn"
                className="rounded-full bg-[#14717A] px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0d5259]"
              >
                Book Appointment
              </Link>
              <a
                href={WHATSAPP_CHAT_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp"
                aria-label="Chat on WhatsApp"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_10px_24px_rgba(37,211,102,0.35)] transition-transform duration-300 hover:-translate-y-1 hover:scale-105"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram"
                aria-label="Follow on Instagram"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#8134af] text-white shadow-[0_10px_24px_rgba(221,42,123,0.35)] transition-transform duration-300 hover:-translate-y-1 hover:scale-105"
              >
                <Instagram className="h-6 w-6" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div>
              <div className="overflow-hidden rounded-3xl border border-[#DCE4E8] shadow-[0_24px_60px_rgba(20,113,122,0.15)]">
                <iframe
                  title="Dr. Vishwa's Dental Hospital on Google Maps"
                  data-testid="contact-map"
                  src={MAP_EMBED_SRC}
                  className="h-[320px] w-full border-0 sm:h-[380px]"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
              <a
                href={MAP_DIRECTIONS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="get-directions-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#14717A] shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                <Navigation className="h-4 w-4" /> Get Directions
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
